/* eslint-disable react-refresh/only-export-components */
import { api, subscribeApiStatus } from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

type ApiStatusContextType = {
  isApiDown: boolean;
  emailValidationEnabled: boolean;
  whatsappValidationEnabled: boolean;
};

const ApiStatusContext = createContext<ApiStatusContextType>({ 
  isApiDown: false,
  emailValidationEnabled: true,
  whatsappValidationEnabled: true,
});

type ApiStatusProviderProps = {
  children: React.ReactNode;
};

export function ApiStatusProvider({ children }: ApiStatusProviderProps) {
  const [isApiDown, setIsApiDown] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeApiStatus((status) => {
      setIsApiDown(status === "down");
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: async () => {
      const response = await api.get<{
        emailValidationEnabled: boolean;
        whatsappValidationEnabled: boolean;
      }>("/config");
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const value = useMemo(() => ({ 
    isApiDown,
    emailValidationEnabled: config?.emailValidationEnabled ?? true,
    whatsappValidationEnabled: config?.whatsappValidationEnabled ?? true,
  }), [isApiDown, config]);

  return <ApiStatusContext.Provider value={value}>{children}</ApiStatusContext.Provider>;
}

export function useApiStatus() {
  return useContext(ApiStatusContext);
}


